import React, { useState, useEffect } from 'react';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import NavigationBreadcrumbs from '../../components/navigation/NavigationBreadcrumbs';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import AccountCard from './components/AccountCard';
import ConnectionModal from './components/ConnectionModal';
import TransactionList from './components/TransactionList';
import NetWorthCard from './components/NetWorthCard';
import SubscriptionTracker from './components/SubscriptionTracker';
import BalanceForecast from './components/BalanceForecast';
import NetWorthTrendChart from './components/NetWorthTrendChart';
import { useAuth } from '../../contexts/AuthContext';
import { financialAccountsService, transactionsService, netWorthService, subscriptionsService } from '../../services/financialDataService';
import { supabase } from '../../lib/supabase';

const FinancialDataAggregation = () => {
  const { user } = useAuth();
  const [connectionModalOpen, setConnectionModalOpen] = useState(false);
  const [selectedView, setSelectedView] = useState('overview');
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);

  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [netWorthData, setNetWorthData] = useState([]);
  const [historicalNetWorthData, setHistoricalNetWorthData] = useState([]);
  const [netWorthTrend, setNetWorthTrend] = useState({
    totalAssets: 0,
    totalLiabilities: 0
  });
  const [currentNetWorth, setCurrentNetWorth] = useState({
    netWorth: 0,
    totalAssets: 0,
    totalLiabilities: 0
  });
  const [subscriptions, setSubscriptions] = useState([]);
  const [forecastData] = useState([
    { date: 'Week 1', balance: 5420, type: 'actual' },
    { date: 'Week 2', balance: 4890, type: 'projected' },
    { date: 'Week 3', balance: 5320, type: 'projected' },
    { date: 'Week 4', balance: 4750, type: 'projected' },
    { date: 'Week 5', balance: 5180, type: 'projected' }
  ]);

  const viewOptions = [
  { value: 'overview', label: 'Overview', description: 'All accounts and insights' },
  { value: 'accounts', label: 'Accounts', description: 'Connected accounts' },
  { value: 'transactions', label: 'Transactions', description: 'Recent activity' },
  { value: 'networth', label: 'Net Worth', description: 'Assets and liabilities' },
  { value: 'subscriptions', label: 'Subscriptions', description: 'Recurring payments' },
  { value: 'forecast', label: 'Forecast', description: 'Balance projections' }];


  const accountFilterOptions = [
  { value: 'all', label: 'All Accounts' },
  ...accounts?.map((acc) => ({
    value: acc?.id,
    label: `${acc?.institutionName} - ${acc?.accountNumber}`
  }))];


  // Fetch all financial data
  useEffect(() => {
    if (!user?.id) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch financial accounts
        const accountsData = await financialAccountsService?.getAll(user?.id);
        setAccounts(accountsData);

        // Fetch transactions
        const transactionsData = await transactionsService?.getAll(user?.id);
        const transformedTransactions = transactionsData?.map(txn => ({
          id: txn?.id,
          merchant: txn?.merchant,
          amount: parseFloat(txn?.amount),
          date: new Date(txn.transactionDate),
          category: txn?.category,
          description: txn?.description,
          isRecurring: false,
          mlSuggestion: null
        }));
        setTransactions(transformedTransactions);

        // Fetch net worth history
        const netWorthHistory = await netWorthService?.getHistory(user?.id, 12);
        const transformedNetWorth = netWorthHistory?.map(nw => ({
          month: new Date(nw.recordedAt)?.toLocaleDateString('en-US', { month: 'short' }),
          value: parseFloat(nw?.netWorth),
          netWorth: parseFloat(nw?.netWorth),
          assets: parseFloat(nw?.assets),
          liabilities: parseFloat(nw?.liabilities)
        }))?.reverse();
        
        setNetWorthData(transformedNetWorth?.map(nw => ({ month: nw?.month, value: nw?.value })));
        setHistoricalNetWorthData(transformedNetWorth);

        // Set current net worth
        if (netWorthHistory?.length > 0) {
          const latest = netWorthHistory?.[0];
          setCurrentNetWorth({
            netWorth: parseFloat(latest?.netWorth),
            totalAssets: parseFloat(latest?.assets),
            totalLiabilities: parseFloat(latest?.liabilities)
          });
          setNetWorthTrend({
            totalAssets: parseFloat(latest?.assets),
            totalLiabilities: parseFloat(latest?.liabilities)
          });
        }

        // Fetch subscriptions
        const subscriptionsData = await subscriptionsService?.getAll(user?.id);
        const transformedSubscriptions = subscriptionsData?.map(sub => ({
          id: sub?.id,
          name: sub?.serviceName,
          logo: 'https://img.rocket.new/generatedImages/rocket_gen_img_1dab497e5-1767166214108.png',
          logoAlt: `${sub?.serviceName} logo`,
          amount: parseFloat(sub?.amount),
          frequency: sub?.billingCycle,
          status: sub?.status,
          nextBilling: new Date(sub.nextBillingDate)
        }));
        setSubscriptions(transformedSubscriptions);
      } catch (error) {
        console.error('Error fetching financial data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Real-time subscriptions
    const accountsChannel = supabase?.channel('accounts_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'financial_accounts',
          filter: `user_id=eq.${user?.id}`
        },
        () => {
          fetchData();
        }
      )?.subscribe();

    const transactionsChannel = supabase?.channel('transactions_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transactions',
          filter: `user_id=eq.${user?.id}`
        },
        () => {
          fetchData();
        }
      )?.subscribe();

    const netWorthChannel = supabase?.channel('net_worth_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'net_worth',
          filter: `user_id=eq.${user?.id}`
        },
        () => {
          fetchData();
        }
      )?.subscribe();

    const subscriptionsChannel = supabase?.channel('subscriptions_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'recurring_subscriptions',
          filter: `user_id=eq.${user?.id}`
        },
        () => {
          fetchData();
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(accountsChannel);
      supabase?.removeChannel(transactionsChannel);
      supabase?.removeChannel(netWorthChannel);
      supabase?.removeChannel(subscriptionsChannel);
    };
  }, [user?.id]);

  const handleSync = async (accountId) => {
    setAccounts(accounts?.map((acc) =>
      acc?.id === accountId ?
      { ...acc, status: 'syncing', lastSynced: new Date() } :
      acc
    ));

    setTimeout(async () => {
      try {
        await financialAccountsService?.update(accountId, {
          lastSynced: new Date(),
          status: 'connected'
        });
        setAccounts(accounts?.map((acc) =>
          acc?.id === accountId ?
          { ...acc, status: 'connected' } :
          acc
        ));
      } catch (error) {
        console.error('Error syncing account:', error);
      }
    }, 2000);
  };

  const handleDisconnect = (accountId) => {
    if (window.confirm('Are you sure you want to disconnect this account?')) {
      setAccounts(accounts?.filter((acc) => acc?.id !== accountId));
    }
  };

  const handleViewTransactions = (accountId) => {
    setSelectedAccount(accountId);
    setSelectedView('transactions');
  };

  const handleConnect = (connectionData) => {
    const newAccounts = connectionData?.accounts?.map((acc) => ({
      id: `acc_${Date.now()}_${Math.random()}`,
      institutionName: connectionData?.institution?.name,
      institutionLogo: connectionData?.institution?.logo,
      institutionLogoAlt: connectionData?.institution?.logoAlt,
      accountType: acc?.name,
      accountNumber: acc?.number,
      balance: acc?.balance,
      availableBalance: acc?.balance,
      status: 'connected',
      lastSynced: new Date(),
      nextSync: new Date(Date.now() + 3600000)
    }));

    setAccounts([...accounts, ...newAccounts]);
  };

  const handleCategorize = (transactionId, category) => {
    console.log(`Categorizing transaction ${transactionId} as ${category}`);
  };

  const handleManageSubscription = (subscriptionId) => {
    console.log(`Managing subscription ${subscriptionId}`);
  };

  const handleRefreshAll = () => {
    setRefreshing(true);
    setAccounts(accounts?.map((acc) => ({ ...acc, status: 'syncing' })));

    setTimeout(() => {
      setAccounts(accounts?.map((acc) => ({
        ...acc,
        status: 'connected',
        lastSynced: new Date()
      })));
      setRefreshing(false);
    }, 3000);
  };

  const totalBalance = accounts?.reduce((sum, acc) => sum + acc?.balance, 0);
  const connectedAccounts = accounts?.filter((acc) => acc?.status === 'connected')?.length;

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation />
      <div className="pt-16">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <NavigationBreadcrumbs />

          <div className="mb-6 md:mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                  Financial Data Aggregation
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Connect and manage your financial accounts with secure Open Banking integration
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="default"
                  iconName="RefreshCw"
                  iconPosition="left"
                  onClick={handleRefreshAll}
                  loading={refreshing}
                  className="flex-1 sm:flex-none">
                  
                  Refresh All
                </Button>
                <Button
                  variant="default"
                  size="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => setConnectionModalOpen(true)}
                  className="flex-1 sm:flex-none">
                  
                  Connect Account
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
              <div className="p-4 md:p-6 bg-card rounded-xl border border-border shadow-elevation-2">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Icon name="Wallet" size={20} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">Total Balance</p>
                    <p className="text-xl md:text-2xl font-bold text-foreground">
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD'
                      })?.format(totalBalance)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-4 md:p-6 bg-card rounded-xl border border-border shadow-elevation-2">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-success/10 flex items-center justify-center">
                    <Icon name="Link" size={20} className="text-success" />
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">Connected Accounts</p>
                    <p className="text-xl md:text-2xl font-bold text-foreground">
                      {connectedAccounts}
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-4 md:p-6 bg-card rounded-xl border border-border shadow-elevation-2">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-warning/10 flex items-center justify-center">
                    <Icon name="Clock" size={20} className="text-warning" />
                  </div>
                  <div>
                    <p className="text-xs md:text-sm text-muted-foreground">Last Synced</p>
                    <p className="text-sm md:text-base font-semibold text-foreground">
                      {new Date(Math.max(...accounts.map((a) => a.lastSynced)))?.toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 md:gap-4 mb-6 md:mb-8">
            <Select
              options={viewOptions}
              value={selectedView}
              onChange={setSelectedView}
              className="w-full sm:w-64" />
            
            {selectedView === 'transactions' &&
            <Select
              options={accountFilterOptions}
              value={selectedAccount}
              onChange={setSelectedAccount}
              className="w-full sm:w-64" />

            }
          </div>

          {selectedView === 'overview' &&
          <div className="space-y-6 md:space-y-8">
              <div>
                <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                  Connected Accounts
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                  {accounts?.map((account) =>
                <AccountCard
                  key={account?.id}
                  account={account}
                  onSync={handleSync}
                  onDisconnect={handleDisconnect}
                  onViewTransactions={handleViewTransactions} />

                )}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                <NetWorthCard netWorthData={netWorthData} trend={netWorthTrend} />
                <SubscriptionTracker
                  subscriptions={subscriptions}
                  transactions={transactions}
                  onManage={handleManageSubscription}
                />
              </div>

              <div className="grid grid-cols-1 gap-6 md:gap-8">
                <NetWorthTrendChart 
                  historicalData={historicalNetWorthData}
                  currentNetWorth={currentNetWorth}
                />
                <BalanceForecast 
                  forecastData={forecastData} 
                  transactions={transactions}
                  confidence={87} 
                />
              </div>
            </div>
          }

          {selectedView === 'accounts' &&
          <div>
              <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                All Accounts
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                {accounts?.map((account) =>
              <AccountCard
                key={account?.id}
                account={account}
                onSync={handleSync}
                onDisconnect={handleDisconnect}
                onViewTransactions={handleViewTransactions} />

              )}
              </div>
            </div>
          }

          {selectedView === 'transactions' &&
          <div>
              <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                Recent Transactions
              </h2>
              <TransactionList
              transactions={transactions}
              onCategorize={handleCategorize}
              onCreateCategory={() => console.log('Create category')} />
            
            </div>
          }

          {selectedView === 'networth' && (
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                Net Worth Analysis
              </h2>
              <div className="space-y-6">
                <NetWorthCard netWorthData={netWorthData} trend={netWorthTrend} />
                <NetWorthTrendChart 
                  historicalData={historicalNetWorthData}
                  currentNetWorth={currentNetWorth}
                />
              </div>
            </div>
          )}

          {selectedView === 'subscriptions' && (
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                Subscription Management
              </h2>
              <SubscriptionTracker
                subscriptions={subscriptions}
                transactions={transactions}
                onManage={handleManageSubscription}
              />
            </div>
          )}

          {selectedView === 'forecast' && (
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">
                Balance Forecast
              </h2>
              <BalanceForecast 
                forecastData={forecastData} 
                transactions={transactions}
                confidence={87} 
              />
            </div>
          )}
        </div>
      </div>
      <ConnectionModal
        isOpen={connectionModalOpen}
        onClose={() => setConnectionModalOpen(false)}
        onConnect={handleConnect} />
      
    </div>);

};

export default FinancialDataAggregation;